/**
 * Короткая сводка критериев для шапки результатов (MVP_PRD §19).
 *
 * Панель критериев показывает пары «название — значение»; здесь нужен другой
 * жанр — цепочка коротких чипов («до 30 000 €», «SUV», «не старше 6 лет»,
 * «для семьи», «надёжность»). Поэтому это отдельная чистая функция, а не
 * переиспользование строк панели: сокращение до чипа — самостоятельное решение
 * представления.
 *
 * Метка жёсткости сохраняется: обязательные условия обязаны визуально
 * отличаться от желательных, иначе список читается как набор равноправных
 * пожеланий (MVP_PRD §19).
 */

import { isUnlimitedBudget, formatCents, formatKilometres } from './criteria';
import {
  bodyTypeKey,
  featureKey,
  fuelKey,
  orderedPriorities,
  priorityKey,
  usageKey,
} from './labels';
import type { CriteriaPayload } from './types';

/** Жёсткость чипа: обязательное условие или предпочтение. */
export type ChipStrength = 'required' | 'preferred';

export interface CriteriaChip {
  /** Уникальный ключ для React и тестов. */
  key: string;
  label: string;
  strength: ChipStrength;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * Чипы подтверждённых критериев в порядке важности для чтения.
 *
 * Незаданный критерий чипа не получает: «без предпочтений» и «вопрос не
 * задавали» одинаково не являются требованием пользователя и в сводку не
 * попадают.
 */
export function criteriaChips(
  payload: CriteriaPayload,
  t: Translate,
  locale: string,
): CriteriaChip[] {
  const chips: CriteriaChip[] = [];
  const add = (key: string, label: string, strength: ChipStrength) => {
    chips.push({ key, label, strength });
  };

  const { budget } = payload;
  if (budget.listing_budget_cents !== null && !isUnlimitedBudget(budget.listing_budget_cents)) {
    add(
      'budget',
      t('results.chip.budget', { value: formatCents(budget.listing_budget_cents, locale) }),
      'required',
    );
  }

  if (payload.body_types.mode === 'required') {
    for (const value of payload.body_types.values) {
      add(`body:${value}`, t(bodyTypeKey(value)), 'required');
    }
  }

  if (payload.max_age.years !== null) {
    add('maxAge', t('criteria.value.years', { count: payload.max_age.years }), 'required');
  }

  for (const value of payload.usage) {
    add(`usage:${value}`, t(usageKey(value)), 'preferred');
  }

  if (payload.annual_mileage_km !== null) {
    add(
      'annualMileage',
      t('criteria.value.kmPerYear', { value: formatKilometres(payload.annual_mileage_km, locale) }),
      'preferred',
    );
  }

  // Топливо: исключение — hard-критерий, список допустимого — тоже.
  if (payload.fuel.excluded.length > 0) {
    add(
      'fuelExcluded',
      t('criteria.fuel.excluded', {
        value: payload.fuel.excluded.map((item) => t(fuelKey(item))).join(', '),
      }),
      'required',
    );
  } else if (payload.fuel.mode === 'allowed_set' && payload.fuel.allowed.length > 0) {
    add('fuelAllowed', payload.fuel.allowed.map((item) => t(fuelKey(item))).join(', '), 'required');
  }
  if (payload.fuel.preferred.length > 0) {
    add(
      'fuelPreferred',
      t('criteria.fuel.preferred', {
        value: payload.fuel.preferred.map((item) => t(fuelKey(item))).join(', '),
      }),
      'preferred',
    );
  }

  if (payload.transmission.value !== null && payload.transmission.mode !== 'any') {
    add(
      'transmission',
      t(`catalog.transmission.${payload.transmission.value}`),
      payload.transmission.mode === 'required' ? 'required' : 'preferred',
    );
  }

  if (payload.drivetrain.value !== null && payload.drivetrain.mode !== 'any') {
    add(
      'drivetrain',
      t(`catalog.drivetrain.${payload.drivetrain.value}`),
      payload.drivetrain.mode === 'required' ? 'required' : 'preferred',
    );
  }

  for (const priority of orderedPriorities(payload.priorities)) {
    add(`priority:${priority.code}`, t(priorityKey(priority.code)), 'preferred');
  }

  for (const code of payload.features.required) {
    add(`feature:${code}`, t(featureKey(code)), 'required');
  }
  for (const code of payload.features.preferred) {
    add(`featurePreferred:${code}`, t(featureKey(code)), 'preferred');
  }

  // Числовые пороги — hard-критерии: неизвестное значение исключает автомобиль.
  const { thresholds } = payload;
  if (thresholds.seats_min !== null) {
    add('seats', t('criteria.threshold.seats_min', { count: thresholds.seats_min }), 'required');
  }
  if (thresholds.trunk_volume_l_min !== null) {
    add(
      'trunk',
      t('criteria.threshold.trunk_volume_l_min', { value: thresholds.trunk_volume_l_min }),
      'required',
    );
  }
  if (thresholds.mileage_km_max !== null) {
    add(
      'mileageMax',
      t('criteria.threshold.mileage_km_max', {
        value: formatKilometres(thresholds.mileage_km_max, locale),
      }),
      'required',
    );
  }
  if (thresholds.power_kw_min !== null) {
    add(
      'power',
      t('criteria.threshold.power_kw_min', { value: thresholds.power_kw_min }),
      'required',
    );
  }

  return chips;
}
